import { useCallback, useEffect, useMemo, useState } from 'react'

import { isArray } from 'lodash'
import { connect, useSelector } from 'react-redux'
import { getUser } from '../../../reducers/UserReducer'
import { getInstitutionSectors } from '../../../utils/api/institution'
import { callFunctionIfDefined } from '../../../utils/functionHelper'
import Selector from './Selector'
import { sortByString } from '../../../utils/arrayUtils'

import '../../../assets/internships-manager.scss'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import InstitutionsSelector from './Institution/InstitutionsSelector'
import { EntityCategories, getEntityCategory } from '../Badges/EntityBadge'

const INSTITUTIONS_STYLE = { width: '100%' }
const SECTOR_STYLE = { width: '175px' }

const mapStateToProps = state => ({ user: getUser(state.getUser) })

const InstitutionsAndSectorsSelector = ({ disabled, institutions, internship, user, onSelect, useLinkedInstitutions }) => {
  const t = useSelector(selectTranslate)

  const [sectors, setSectors] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedInstitution, setSelectedInstitution] = useState(null)

  useEffect(() => {
    if (internship.institution) {
      setLoading(true)

      getInstitutionSectors(user, internship.institution).then(json => {
        if (json && json.data) {
          setSectors(sortByString(json.data, 'name'))
        }

        setLoading(false)
      })
    } else {
      setSectors([])
    }
  }, [internship.institution?.id])

  const handleInstitutionSelected = (value, option, institution) => {
    setSelectedInstitution(institution ?? null)
    callFunctionIfDefined(onSelect, [{ institution: institution ?? null, sector: null }])
  }

  const handleSectorSelected = value => {
    const sector = sectors.find(s => s.id === value)

    callFunctionIfDefined(onSelect, [{ institution: selectedInstitution ?? internship.institution, sector }])
  }

  /**
   * The institution of the internship is not necessarily part of the list handed by the school : it is
   * put back so that the select always holds the option labelling its own value, rather than falling
   * back on the raw id. The list is empty until the school has loaded its institutions, so the case is
   * the ordinary one on the first render.
   */
  const selectableInstitutions = useMemo(() => {
    const current = internship.institution

    if (!isArray(institutions) || !current || institutions.some(i => i.id === current.id)) {
      return institutions
    }

    return [...institutions, current]
  }, [institutions, internship.institution])

  /** Only the institutions this school is linked to can be picked when the option is on. */
  const isInstitutionDisabled = useCallback(
    institution => useLinkedInstitutions && getEntityCategory(institution) === EntityCategories.OTHERS,
    [useLinkedInstitutions]
  )

  return (
    <div className='internship-institution-sector-wrapper'>
      <InstitutionsSelector
        disabled={disabled}
        empty
        institutions={selectableInstitutions}
        isDisabled={isInstitutionDisabled}
        placeholder={t('institution')}
        reverse
        selectorClassName='narrow'
        style={INSTITUTIONS_STYLE}
        values={internship.institution?.id ?? null}
        onChange={handleInstitutionSelected}
      />
      {!loading && (
        <Selector
          data={sectors}
          dataKey='name'
          disabled={disabled || !sectors.length}
          onSelect={handleSectorSelected}
          showArrow={false}
          showEmpty
          showSearch
          style={SECTOR_STYLE}
          value={!internship.sector || internship.sector.parent === null ? -1 : internship.sector.id}
        />
      )}
      {loading && (
        <div className='flex-center'>
          <div className='loading-icon black' />
        </div>
      )}
    </div>
  )
}

export default connect(mapStateToProps)(InstitutionsAndSectorsSelector)
